// Publicar un lanzamiento, fuera de la pantalla — fase D de
// ADR-2026-09-21-publicar-un-lanzamiento-desde-el-chat.
//
// Esto vivía adentro de `LanzamientoDetalle.tsx`, mezclado con los `setAviso` y el
// polling de la barra de progreso, así que no se podía llamar desde ningún otro lado.
// Acá quedan los pasos, en orden, sin React: revisar qué le falta a cada ficha, ver que
// la tienda siga conectada, firmar las fotos, armar el pedido CON el árbol de categorías
// y dispararlo.
//
// **El árbol de categorías se pasa como una función que lo trae, no como un dato.** Es
// la parte delicada de publicar: al crear una categoría en Tiendanube, el worker le
// escribe el `tn_category_id` a la fila de Atlas, y un árbol leído ANTES de eso viaja con
// esos ids en null — publicar de nuevo con él crearía las mismas categorías DUPLICADAS
// en la tienda. La pantalla lo resolvía recargando el árbol al terminar; con una función
// que lee en el momento, cada publicación parte de lo que hay guardado y el problema no
// existe. Es también lo que hace que esto se pueda llamar desde el chat, que no tiene
// ningún árbol en memoria.
import {
  deshacerPublicando as deshacerPublicandoDefault,
  marcarPublicando as marcarPublicandoDefault,
  payloadDeExport,
  problemasParaPublicar,
  type CategoriaDeTienda,
  type LanzamientoDetalle,
  type ProblemaDePublicacion,
  type ProductoDeLanzamiento,
} from './lanzamientosApi';
import type { TnExportProducto } from '../../lib/tiendaNube';
import type { ProductoEnLaTienda } from './cambiosContraLaTienda';
import { faltaParaMl, productoParaMl } from '../../lib/mercadoLibrePublicarLogica';

/** El backend no lo mandó porque ya se está publicando en esa tienda (otro pedido en curso). */
export const YA_PUBLICANDO = 'ya_publicando';

export interface DependenciasDePublicar {
  /** Si la tienda de esa marca sigue conectada. `null` = no se pudo averiguar. */
  estadoDeLaTienda: () => Promise<{ connected: boolean } | null>;
  /** Firma el original de una foto para que Tiendanube pueda bajarla. */
  firmar: (fileName: string) => Promise<string>;
  /** El árbol de categorías de la marca, LEÍDO AHORA (ver el comentario de arriba). */
  categorias: () => Promise<readonly CategoriaDeTienda[]>;
  /**
   * Lo que HOY está publicado de los productos que ya están en la tienda, leído en el momento: los
   * cambios se calculan contra eso, no contra una foto vieja. null = no se pudo leer
   * (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento).
   */
  publicados: (tnProductIds: number[]) => Promise<ReadonlyMap<number, ProductoEnLaTienda> | null>;
  /** Deja el lanzamiento y los productos que se mandan en "publicando". */
  marcarPublicando?: (lanzamientoId: string, lanzamientoProductoIds: string[]) => Promise<void>;
  /** Lo deshace si el pedido no llegó a salir (review Greptile #127). */
  deshacerPublicando?: (lanzamientoId: string) => Promise<void>;
  /**
   * Manda el pedido. `ok: false` = el backend no lo aceptó y no va a pasar nada. Con el id del
   * lanzamiento, el worker anota cómo terminó cada producto.
   */
  disparar: (productos: TnExportProducto[], lanzamientoId: string) => Promise<{ ok: boolean; motivo?: string }>;
}

export type ResultadoDePublicar =
  /** A alguna ficha le falta algo. No se tocó nada. */
  | { estado: 'faltan'; problemas: ProblemaDePublicacion[] }
  /** La tienda no está conectada (o no se pudo averiguar). */
  | { estado: 'sin-tienda' }
  /** Ninguna foto se pudo firmar: no hay nada que mandar. */
  | { estado: 'sin-fotos' }
  /** Los productos ya están como en la tienda y no tienen fotos nuevas: no hay nada que mandar. */
  | { estado: 'sin-cambios' }
  /** No se pudo leer cómo están en la tienda los que ya existen: sin eso no se sabe qué cambiar. */
  | { estado: 'sin-lectura' }
  /** El backend no lo aceptó. En la tienda no pasó nada. */
  | { estado: 'rechazado'; motivo?: string }
  /** Salió. Tarda: el worker va creando y subiendo. */
  | { estado: 'lanzado'; productos: number; imagenes: number; seCrean: number; seEditan: number };

/**
 * Publica un lanzamiento en la tienda: los productos que vienen en `detalle.productos_`.
 * La pantalla pasa sólo los que la persona dejó tildados; el chat, todos.
 *
 * Es fire-and-forget, como todo lo que pasa por el export: cuando esto vuelve con
 * `lanzado`, el worker recién empieza. Quien llame decide si espera (la pantalla mira el
 * progreso) o si sólo lo cuenta (el chat).
 */
export async function publicarLanzamiento(
  detalle: LanzamientoDetalle,
  deps: DependenciasDePublicar,
): Promise<ResultadoDePublicar> {
  // Contra TODAS las tiendas del lanzamiento: la ficha es una sola (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
  const problemas = problemasParaPublicar(detalle.productos_, detalle.destinos ?? [detalle.canal]);
  if (problemas.length > 0) return { estado: 'faltan', problemas };

  // Sin tienda conectada el backend responde 400 y la persona no se entera
  // (ADR-2026-08-04-exportar-tiendanube-siempre-visible).
  const estado = await deps.estadoDeLaTienda();
  if (!estado?.connected) return { estado: 'sin-tienda' };

  const urls = new Map<string, string>();
  await Promise.all(
    detalle.productos_.flatMap((p) =>
      p.imagenes.map(async (i) => {
        const url = await deps.firmar(i.fileName);
        // Una foto subida no tiene fila de generación: se identifica por su archivo.
        // Misma clave que usa `payloadDeExport`.
        if (url) urls.set(i.imagenGeneradaId ?? i.fileName, url);
      }),
    ),
  );

  const existentes = detalle.productos_.flatMap((p) => (p.productoId && p.tnProductId != null ? [p.tnProductId] : []));
  let publicados: ReadonlyMap<number, ProductoEnLaTienda> = new Map();
  if (existentes.length > 0) {
    const leidos = await deps.publicados(existentes);
    // Sin saber qué hay en la tienda no se manda nada: mandar la ficha a ciegas pisaría lo de allá.
    if (!leidos) return { estado: 'sin-lectura' };
    publicados = leidos;
  }

  const productos = payloadDeExport(
    detalle.productos_,
    urls,
    await deps.categorias(),
    publicados,
  ) as TnExportProducto[];
  if (productos.length === 0) {
    const algunaFoto = detalle.productos_.some((p) => p.imagenes.length > 0);
    return { estado: algunaFoto ? 'sin-fotos' : 'sin-cambios' };
  }

  // Se marca ANTES de disparar porque el worker puede arrancar en cualquier momento y la
  // pantalla necesita el estado desde el primer segundo. Pero si el pedido no sale, hay
  // que deshacerlo: sin eso el lanzamiento queda clavado en "Publicando" para siempre,
  // con sus spinners, y el reintento arranca de un estado falso (review Greptile #127).
  await (deps.marcarPublicando ?? marcarPublicandoDefault)(
    detalle.id,
    productos.map((p) => p.lanzamiento_producto_id).filter((x): x is string => !!x),
  );
  const disparo = await deps.disparar(productos, detalle.id);
  if (!disparo.ok) {
    // `ya_publicando`: otro pedido lo está publicando (el chat, otra pestaña). Su "publicando" es real,
    // así que no se deshace; el backend no lo mandó de nuevo para no duplicar (caza de bugs del 1/10).
    if (disparo.motivo !== YA_PUBLICANDO) await (deps.deshacerPublicando ?? deshacerPublicandoDefault)(detalle.id);
    return { estado: 'rechazado', motivo: disparo.motivo };
  }

  return {
    estado: 'lanzado',
    productos: productos.length,
    imagenes: productos.reduce((n, p) => n + p.imagenes.length, 0),
    // Los que todavía no existen en la tienda: es lo que le importa saber a la persona,
    // porque esos aparecen NUEVOS y a la venta.
    seCrean: productos.filter((p) => p.action === 'create').length,
    // Los que ya estaban y se les cambia la ficha.
    seEditan: productos.filter((p) => p.cambios !== undefined).length,
  };
}

// ── Mercado Libre (ADR-2026-09-30-publicar-en-mercado-libre-desde-el-chat) ──────────────────
//
// Los mismos pasos para la pantalla y para el chat, igual que con Tiendanube: lo que frena
// antes de mandar nada (la cuenta y la ficha de Mercado Libre) y el pedido al backend, que crea
// una publicación por color × talle y anota cómo le fue a cada producto.

/** Los productos que viajan: los que tienen producto de Atlas y alguna foto. */
export function productosParaMl(detalle: LanzamientoDetalle): ProductoDeLanzamiento[] {
  return detalle.productos_.filter((p) => p.productoId && p.imagenes.length > 0);
}

/** Cuántas publicaciones crea un producto en Mercado Libre: una por variante (color × talle). */
export function publicacionesDe(p: ProductoDeLanzamiento): number {
  return Math.max(1, p.variantes?.length ?? 0);
}

/** Ya publicado en Mercado Libre desde Atlas: volver a publicarlo encuentra su SKU y no crea otra. */
export function yaPublicadoEnMl(p: ProductoDeLanzamiento): boolean {
  return p.porDestino?.mercadolibre?.estado === 'publicado';
}

export type FrenoDeMl =
  | { motivo: 'sin-cuenta' }
  | { motivo: 'falta'; producto: ProductoDeLanzamiento; texto: string };

/**
 * Lo que frena la publicación en Mercado Libre ANTES de mandar nada a ninguna tienda. null = puede
 * salir (lo demás lo dice Mercado Libre al validar cada publicación).
 */
export function frenoDeMl(detalle: LanzamientoDetalle, cuentaConectada: boolean): FrenoDeMl | null {
  if (!cuentaConectada) return { motivo: 'sin-cuenta' };
  for (const p of productosParaMl(detalle)) {
    const texto = faltaParaMl(p, p.mercadolibre);
    if (texto) return { motivo: 'falta', producto: p, texto };
  }
  return null;
}

export type ResultadoDePublicarMl =
  | FrenoDeMl
  | { motivo: 'sin-fotos' }
  | { motivo: 'rechazado'; texto: string }
  | { motivo: 'lanzado'; productos: number; publicaciones: number };

export interface DependenciasDePublicarMl {
  /** Si la cuenta de Mercado Libre de la marca está conectada. */
  cuentaConectada: boolean;
  /** Manda el pedido al backend; tira si no lo aceptó. */
  publicar: (productos: Array<Record<string, unknown>>, lanzamientoId: string) => Promise<void>;
}

export async function publicarLanzamientoEnMl(
  detalle: LanzamientoDetalle,
  deps: DependenciasDePublicarMl,
): Promise<ResultadoDePublicarMl> {
  const freno = frenoDeMl(detalle, deps.cuentaConectada);
  if (freno) return freno;
  const productos = productosParaMl(detalle);
  if (productos.length === 0) return { motivo: 'sin-fotos' };
  try {
    await deps.publicar(
      productos.map((p) => productoParaMl(p, p.mercadolibre!)),
      detalle.id,
    );
  } catch (e) {
    return { motivo: 'rechazado', texto: e instanceof Error ? e.message : 'no se pudo publicar' };
  }
  return {
    motivo: 'lanzado',
    productos: productos.length,
    publicaciones: productos.reduce((n, p) => n + publicacionesDe(p), 0),
  };
}
